/* eslint react/style-prop-object: 0 */
import React, { useState } from "react";
import { Attention, Button, Typography } from "@optimizely/axiom";

import { IdentityProvider, ISAMLIdp, SAMLIdpUpdateFields } from "../../../../domain/IdentityProvider";
import { ConfirmationDialog } from "../../ConfirmationDialog/ConfirmationDialog";
import { datadogRum } from "@datadog/browser-rum";
import { SSOSamlEditSheet } from "../SSOSamlEditSheet/SSOSamlEditSheet";
import { emitToast } from "../../../../lib/toaster-utils";
import { UploadButton } from "../../UploadButton/UploadButton";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../../feature-flags/flags";

export const SSOConnectionDetails = ({
    className = "",
    identityProvider: connectionIdp,
    onRemove,
    onUpdate,
    onCertificateUpload,
    showWarningWhenRemoving = false
}: {
    className?: string;
    identityProvider: IdentityProvider;
    onRemove: () => Promise<void>;
    onCertificateUpload: ({
        certificate,
        identityProviderId
    }: {
        certificate: File;
        identityProviderId: string;
    }) => Promise<void>;
    onUpdate: (updatedData: SAMLIdpUpdateFields) => Promise<void>;
    showWarningWhenRemoving?: boolean;
}) => {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [deletionError, setDeletionError] = useState("");
    const [deletionInProgress, setDeletionInProgress] = useState(false);
    const [uploadInProgress, setUploadInProgress] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadedFileKey, setUploadedFileKey] = useState(0);
    const [showEditSheet, setShowEditSheet] = useState(false);
    const { enabled: isScimUiEnabled } = useFeatureFlag(Flags.ENABLE_SCIM_UI);

    const handleRemove = async () => {
        setDeletionInProgress(true);
        try {
            await onRemove();
            setShowConfirmation(false);
        } catch (error: any) {
            datadogRum.addError(error);
            setDeletionError("An error occurred while deleting your SSO Connection");
        } finally {
            setDeletionInProgress(false);
        }
    };

    const handleFileUpload = async (file: File) => {
        setUploadError(null);
        setUploadInProgress(true);
        try {
            await onCertificateUpload({ certificate: file, identityProviderId: connectionIdp.id });
            emitToast({
                type: "good-news",
                message: "Certificate uploaded successfully."
            });
            setUploadedFileKey((prev) => prev + 1);
        } catch (error: any) {
            datadogRum.addError(error);
            setUploadError("An error occurred while uploading the certificate.");
            emitToast({
                type: "bad-news",
                message: "An error occurred while uploading the certificate."
            });
        } finally {
            setUploadInProgress(false);
        }
    };

    const handleUpdate = async (fields: SAMLIdpUpdateFields) => {
        await onUpdate(fields);
        setShowEditSheet(false);
    };

    const isSAML = connectionIdp!.type === "SAML";
    const samlProvider = connectionIdp as ISAMLIdp;

    return (
        <div className={`flex flex--column flex-align--start ${className}`}>
            <>
                <Typography type="header4">SSO Connection Details</Typography>
                <Typography tag="p" type="caption">
                    Use the following information for your SSO provider to complete setup.
                </Typography>
                {isSAML && (
                    <>
                        <Typography type="caption">Audience URL</Typography>
                        <Typography type="xs">
                            <span style={{ color: "#707070" }}>{samlProvider.audienceUrl}</span>
                        </Typography>

                        <Typography className="push-double--top" type="caption">
                            Assertion Consumer Service URL
                        </Typography>
                        <Typography type="xs">
                            <span style={{ color: "#707070" }}>{samlProvider.acsUrl}</span>
                        </Typography>
                    </>
                )}
                <Typography className="push-double--top" type="caption">
                    IDP-Initiated URL
                </Typography>
                <Typography type="xs">
                    <span style={{ color: "#707070" }}>{`${process.env.REACT_APP_HOME_APP_URL}/?idpId=${
                        connectionIdp!.idpId
                    }`}</span>
                </Typography>
                {isSAML && (
                    <>
                        <Typography className="push-double--top" type="caption">
                            Certificate Expiration
                        </Typography>
                        <Typography type="xs">
                            <span style={{ color: "#707070" }}>
                                {samlProvider.expiresAt
                                    ? new Date(samlProvider.expiresAt).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "2-digit",
                                          year: "numeric"
                                      })
                                    : "Unknown"}
                            </span>
                        </Typography>
                    </>
                )}
            </>
            <div className="flex flex-column flex-gap--m push-double--top width-1-1">
                {isSAML && (
                    <UploadButton
                        key={uploadedFileKey}
                        className="width-1-1 push--bottom"
                        extensions="text/plain,.pem,.p12,.pfx,.crt,.cer,.ca-bundle,.p7b,.p7c,.p7s"
                        onSelection={handleFileUpload}
                        disabled={uploadInProgress || deletionInProgress}
                        isLoading={uploadInProgress}
                        leftIcon={"upload"}
                    >
                        Upload New Certificate
                    </UploadButton>
                )}

                <div className="flex flex-gap--m width-1-1 flex--row">
                    {isSAML && (
                        <Button
                            className="push-half--right width-1-2"
                            isDisabled={showConfirmation || deletionInProgress || uploadInProgress}
                            style="outline"
                            onClick={() => setShowEditSheet(true)}
                        >
                            Edit Connection
                        </Button>
                    )}
                    <Button
                        className="width-1-2"
                        style="danger-outline"
                        isDisabled={showConfirmation || deletionInProgress || uploadInProgress}
                        onClick={() => setShowConfirmation(true)}
                    >
                        Remove Connection
                    </Button>
                </div>
            </div>
            {uploadError && (
                <Typography type="xs" className="push--top error-text">
                    {uploadError}
                </Typography>
            )}
            {showConfirmation && (
                <>
                    <ConfirmationDialog
                        onCancel={() => setShowConfirmation(false)}
                        onConfirmation={handleRemove}
                        callToActionText="Remove"
                        error={deletionError}
                        loading={deletionInProgress}
                        title="Remove SSO Connection"
                    >
                        <Typography type="body" className="push-double--bottom">
                            Your SSO connection is currently active. Removing it will cause a disruption to services.
                        </Typography>
                        {(showWarningWhenRemoving || isScimUiEnabled) && (
                            <Attention
                                type="warning"
                                className="push-double--bottom"
                                data-testid="sso-removal-warnings"
                            >
                                <Typography type="body">
                                    <strong className="weight--700">Warning:</strong>
                                    <ul className="warning-list push-half--top push-half--bottom">
                                        {showWarningWhenRemoving && (
                                            <li className="warning-list-item">
                                                Your account will be reconfigured for local authentication and all users
                                                will need to set up a new password from the activation email that will
                                                be sent.
                                            </li>
                                        )}
                                        {isScimUiEnabled && (
                                            <li className="warning-list-item">
                                                Removing this SSO connection will also delete any associated SCIM
                                                instance connections.
                                            </li>
                                        )}
                                    </ul>
                                </Typography>
                                <Typography type="body">
                                    <strong className="weight--700">Note:</strong> Existing domains will{" "}
                                    <strong className="weight--700">not</strong> be removed.
                                </Typography>
                            </Attention>
                        )}
                    </ConfirmationDialog>
                </>
            )}
            {isSAML && showEditSheet && (
                <SSOSamlEditSheet idp={samlProvider} onClose={() => setShowEditSheet(false)} onSave={handleUpdate} />
            )}
        </div>
    );
};

SSOConnectionDetails.displayName = "SSOConnectionDetails";
